

import React, { useState, useEffect } from 'react';
import { Menu, X, Calendar, Sparkles } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isClientPage = location.pathname === '/client';
  
  const logoSrc = 'logo_b_w.png';
  
  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Base menu items for the coach page with updated href links
  const coachMenuItems = [
    { title: 'Features', href: '#features' },
    { title: 'Success Stories', href: '#testimonials' },
    { title: 'FAQ', href: '#faq' },
    { title: 'Client App', href: '/client' }
  ];
  
  // Menu items for the client page
  const clientMenuItems = [
    { title: 'For Coaches', href: '/' },
    { title: 'Highlights', href: '#highlights' },
    { title: 'Testimonials', href: '#testimonials' }
  ];
  
  // Choose the appropriate menu items based on the current page
  const menuItems = isClientPage ? clientMenuItems : coachMenuItems;

  return (
    <header className={`sticky top-0 z-50 transition-all duration-500 ${
      scrolled 
        ? 'bg-white/95 backdrop-blur-lg shadow-lg border-b border-[#c4ffa0]/20' 
        : 'bg-white/90 backdrop-blur-md border-b border-gray-100'
    }`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6">
          {/* Enhanced Logo and Brand */}
          <div className="flex items-center flex-shrink-0">
            <a href="/" className="flex items-center group">
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#c4ffa0]/20 to-[#c4ffa0]/10 p-1">
                <img
                  src={logoSrc}
                  alt="kinOS"
                  className="h-9 w-9 object-contain transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#c4ffa0]/30 to-green-500/20 opacity-0 group-hover:opacity-100 transition-all duration-500 rounded-xl"></div>
                <div className="absolute -inset-1 bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-xl opacity-0 group-hover:opacity-20 blur-sm transition-all duration-500"></div>
              </div>
              <span className="ml-3 text-2xl font-bold tracking-tight text-gray-900 group-hover:text-[#2d4a1e] transition-colors duration-300">
                kin<span className="text-[#2d4a1e] relative">
                  OS
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#c4ffa0] rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 animate-pulse"></div>
                </span>
              </span>
              {/* {location.pathname === '/' && (
                <div className="ml-2 bg-gradient-to-r from-[#c4ffa0]/20 to-green-100/30 backdrop-blur-sm text-[#2d4a1e] text-xs px-2.5 py-1 rounded-full border border-[#c4ffa0]/30 shadow-sm hover:shadow-md transition-all duration-300 flex items-center">
                  <Sparkles className="w-3 h-3 mr-1" />
                  <span className="font-medium">beta</span>
                </div>
              )} */}
            </a>
          </div>

          {/* Enhanced Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2.5 rounded-xl transition-all duration-300 ${
                isMenuOpen 
                  ? 'text-[#2d4a1e] bg-[#c4ffa0]/20 rotate-180 scale-110' 
                  : 'text-gray-700 hover:text-[#2d4a1e] hover:bg-[#c4ffa0]/20 hover:scale-105'
              }`}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Enhanced Desktop Navigation */}
          <nav className="hidden md:flex items-center justify-center flex-1">
            <div className="flex space-x-8">
              {menuItems.map((item, index) => (
                <a 
                  key={index} 
                  href={item.href} 
                  className="text-base font-medium text-gray-700 hover:text-[#2d4a1e] transition-all duration-300 relative group py-2"
                >
                  {item.title}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#c4ffa0] to-green-400 group-hover:w-full transition-all duration-500 rounded-full" />
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c4ffa0]/50 group-hover:w-full transition-all duration-300 delay-100 rounded-full blur-sm" />
                </a>
              ))}
            </div>
          </nav>
         
          {/* Enhanced Call to Action Button */}
          <div className="hidden md:flex items-center">
            <a 
              href="https://calendly.com/usekinos"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-xl"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#c4ffa0] to-green-400 opacity-0 group-hover:opacity-100 transition-all duration-500 blur-md"></div>
              <button className="relative w-full sm:w-auto bg-gradient-to-r from-[#c4ffa0] to-[#c4ffa0]/90 hover:from-[#c4ffa0]/90 hover:to-[#c4ffa0] text-[#2d4a1e] text-base px-6 py-2.5 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center group-hover:shadow-[#c4ffa0]/25">
                <Calendar className="w-4 h-4 mr-2 text-[#2d4a1e] transition-transform duration-300 group-hover:rotate-12" />
                <span>Get Free Trial</span>
                <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              </button>
            </a>
          </div>
        </div>

        {/* Enhanced Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-lg border-t border-[#c4ffa0]/20 shadow-xl animate-slideDown">
            <div className="px-4 pt-4 pb-6 space-y-2">
              {menuItems.map((item, index) => (
                <a 
                  key={index} 
                  href={item.href} 
                  className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:text-[#2d4a1e] hover:bg-gradient-to-r hover:from-[#c4ffa0]/10 hover:to-[#c4ffa0]/5 transition-all duration-300 relative group"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <span className="relative z-10">{item.title}</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#c4ffa0]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                </a>
              ))}
              
              <div className="pt-6 px-2">
                <a 
                  href="https://calendly.com/usekinos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full group relative overflow-hidden rounded-xl"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#c4ffa0] to-green-400 opacity-0 group-hover:opacity-20 transition-all duration-500 blur-sm"></div>
                  <button className="relative w-full bg-gradient-to-r from-[#c4ffa0] to-[#c4ffa0]/90 hover:from-[#c4ffa0]/90 hover:to-[#c4ffa0] text-[#2d4a1e] text-base px-6 py-3.5 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center group-hover:scale-[1.02]">
                    <Calendar className="w-5 h-5 mr-2 text-[#2d4a1e] transition-transform duration-300 group-hover:rotate-12" />
                    <span>Get Free Trial</span>
                    <Sparkles className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-all duration-300 text-[#2d4a1e]" />
                  </button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </header>
  );
};

export default Header;